import expandCollapsedItemsRollUp from './expandCollapsedItemsRollUp';
import datapoints from '../datapoints';
import ConversationReadingPaneViewState from '../store/schema/ConversationReadingPaneViewState';
import readingPaneStore from '../store/Store';
import { hasCollapsedItemsRollUp } from '../utils/rollUp/collapsedItemsRollUpUtils';
import { ObservableMap } from 'mobx';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { ClientItemId } from 'owa-client-ids';
import { LOAD_MORE_INCREMENT, loadConversation } from 'owa-mail-store-actions';
import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import mailStore from 'owa-mail-store/lib/store/Store';
import truncateCountForDataPointAggregation from 'owa-mail-store/lib/utils/truncateCountForDataPointAggregation';
import { select } from 'satcheljs/lib/legacy';

export interface LoadMoreState {
    conversations: ObservableMap<string, ConversationItemParts>;
    conversationReadingPaneViewState: ConversationReadingPaneViewState;
}

export default createLegacyActionWithDatapoint('loadMore', datapoints.RPCountLoadMore)(
    select<LoadMoreState>({
        conversations: () => mailStore.conversations,
        conversationReadingPaneViewState: () => readingPaneStore.conversationReadingPaneViewState,
    })(function loadMore(conversationId: ClientItemId, state?: LoadMoreState): Promise<void> {
        if (conversationId && state.conversations.has(conversationId.Id)) {
            const conversationItemParts = state.conversations.get(conversationId.Id);

            conversationItemParts.isLoadMoreInProgress = true;
            conversationItemParts.maxItemsToReturn =
                conversationItemParts.maxItemsToReturn + LOAD_MORE_INCREMENT;
            conversationItemParts.syncState = '';

            // Calculate how many times the user has clicked "loadMore" for this conversation.
            const dataPoint = returnTopExecutingActionDatapoint();
            if (dataPoint) {
                // Null check for tests.
                dataPoint.addCustomData([
                    getLoadMoreIteration(conversationItemParts.maxItemsToReturn),
                ]);
            }

            // Expand the collapsed items roll up if it's existed.
            // This is the case when user clicks "load more" from the list view expanded item list
            if (hasCollapsedItemsRollUp(state.conversationReadingPaneViewState)) {
                expandCollapsedItemsRollUp(state.conversationReadingPaneViewState, true /*isAuto*/);
            }

            return loadConversation(conversationId, 'LoadMore');
        }

        return Promise.resolve();
    })
);

function getLoadMoreIteration(maxItemsToReturn: number): number {
    // Ex: maxItemsToReturn = 40. (40-20)/10 = 2. This is the second time the user has clicked load more.
    // The ceil function should be unnecessary. It's a precaution so future changes don't put us over the 24 unique value count mentioned below.
    const iteration = Math.ceil((maxItemsToReturn - 20) / 10);

    // For datapoint aggregation in azure we can only have 24 unique possible values for any custom data key.
    // Accounting for 0, bucket all values >=23 as "23".
    return truncateCountForDataPointAggregation(iteration);
}
